import { createTheme, CssBaseline, PaletteColorOptions, PaletteOptions, ThemeProvider } from '@mui/material'
import { deepPurple, orange, pink } from '@mui/material/colors'
import { useAppSelector } from '../../store/Hooks'
import UIInitializer from './UIInitializer'

declare module '@mui/material/styles/createPalette' {
    interface TypeBackground {
        smartable: string
    }
}

declare module '@mui/material/styles' {
    interface TypographyVariants {
        important: React.CSSProperties
    }
    interface TypographyVariantsOptions {
        important?: React.CSSProperties
    }
}

declare module '@mui/material/Typography' {
    interface TypographyPropsVariantOverrides {
        important: true
    }
}

declare module '@mui/material/styles' {
    interface Palette {
        audit: PaletteColor
        dryRun: PaletteColor
        subFlow: PaletteColor
    }

    interface PaletteOptions {
        audit: PaletteColorOptions
        dryRun: PaletteColorOptions
        subFlow: PaletteColorOptions
    }
}

declare module '@mui/material/Button' {
    interface ButtonPropsColorOverrides {
        audit: true
        dryRun: true
        subFlow: true
    }
}

declare module '@mui/material/Typography' {
    interface TypographyPropsColorOverrides {
        dryRun: true
        audit: true
        subFlow: true
    }
}

declare module '@mui/material/CircularProgress' {
    interface CircularProgressPropsColorOverrides {
        dryRun: true
        audit: true
        subFlow: true
    }
}

declare module '@mui/material/SvgIcon' {
    interface SvgIconPropsColorOverrides {
        audit: true
        dryRun: true
        subFlow: true
    }
}

declare module '@mui/material/TextField' {
    interface TextFieldPropsColorOverrides {
        audit: true
        dryRun: true
        subFlow: true
    }
}

declare module '@mui/material/Radio' {
    interface RadioPropsColorOverrides {
        audit: true
        dryRun: true
        subFlow: true
    }
}

declare module '@mui/material/FormLabel' {
    interface FormLabelPropsColorOverrides {
        audit: true
        dryRun: true
        subFlow: true
    }
}

declare module '@mui/material/Chip' {
    interface ChipPropsColorOverrides {
        audit: true
        dryRun: true
        subFlow: true
    }
}

declare module '@mui/material/styles' {
    interface PaletteColor {
        focus?: string
        selected?: string
        focusVisible?: string
    }
    interface SimplePaletteColorOptions {
        focus?: string
        selected?: string
        focusVisible?: string
    }
}

const primitiveColors = {
    primary: {
        950: '#002E6D',
        900: '#0048B3',
        800: '#004FE6',
        700: '#005DFF',
        600: '#0078FF',
        500: '#00A2FF',
        400: '#26C9FF',
        300: '#6BDFFF',
        200: '#A7E9FF',
        100: '#CEF1FF',
        50: '#E9F9FF'
    },
    secondary: {
        950: '#07324A',
        900: '#0A4E70',
        800: '#056087',
        700: '#0078AE',
        600: '#008ECB',
        500: '#0AB1ED',
        400: '#34C7FC',
        300: '#7BDAFE',
        200: '#B9E9FE',
        100: '#DFF3FF',
        50: '#F0FAFF'
    },
    error: {
        950: '#420F18',
        900: '#772435',
        800: '#8B2638',
        700: '#A62A3D',
        600: '#BB3646',
        500: '#DA5962',
        400: '#E78589',
        300: '#F0BAB3',
        200: '#F7D4D5',
        100: '#FBE9E8',
        50: '#FDF3F3'
    },
    success: {
        950: '#023116',
        900: '#0F582E',
        800: '#106B34',
        700: '#0C843C',
        600: '#0BAE4B',
        500: '#15D260',
        400: '#3EEA82',
        300: '#7EF7AD',
        200: '#B6FCD1',
        100: '#D9FFE7',
        50: '#EFFEF4'
    }
}

const darkPalette: PaletteOptions = {
    mode: 'dark',
    background: {
        default: '#1e1e1e',
        paper: '#171717',
        smartable: '#1e1e1e'
    },
    text: {
        primary: 'rgba(255, 255, 255)',
        secondary: 'rgba(255, 255, 255, .6)',
        disabled: 'rgba(255, 255, 255, .38)'
    },
    primary: {
        main: '#007F89',
        dark: '#248E9A',
        light: '#96FFFB',
        focus: 'rgb(0, 127, 137,0.16)',
        focusVisible: 'rgb(0, 127, 137,0.3)',
        selected: 'rgb(0, 127, 137,0.12)',
        contrastText: 'rgba(255, 255, 255)',
        ...primitiveColors.primary
    },
    secondary: {
        main: '#5BEEE1',
        dark: '#2ADECE',
        light: '#CCFFFF',
        contrastText: '#002732',
        ...primitiveColors.secondary
    },
    action: {
        /*   hover: 'rgba(255, 255, 255, .8)',
          selected: 'rgba(255, 255, 255, .16)', */
    },
    error: {
        main: '#DA5962',
        dark: '#BB3646',
        light: '#F0B1B3',
        contrastText: '#FFFFFF',
        ...primitiveColors.error
    },
    warning: {
        main: '#FFA726',
        dark: '#F57C00',
        light: '#FFB74D',
        contrastText: 'rgba(0, 0, 0, .87)'
    },
    info: {
        main: '#6496A5',
        dark: '#2D7183',
        light: '#4FC3F7',
        contrastText: 'rgba(0, 0, 0, .87)'
    },
    success: {
        main: '#3EEA82',
        dark: '#15D260',
        light: '#7EF7AD',
        contrastText: 'rgba(0, 0, 0, .87)',
        ...primitiveColors.success
    },
    audit: {
        main: deepPurple['A200'],
        contrastText: '#FFFFFF'
    },
    dryRun: {
        main: pink['300'],
        contrastText: '#000000'
    },
    subFlow: {
        main: orange['700'],
        contrastText: '#000000'
    },
    common: {}
}

const lightPalette: PaletteOptions = {
    mode: 'light',
    background: {
        default: '#fbfbfb',
        paper: '#ffffff',
        smartable: '#F5F5F5'
    },
    text: {
        primary: 'rgba(0, 0, 0, .87)',
        secondary: 'rgba(0, 0, 0, .60)',
        disabled: 'rgba(0, 0, 0, .38)'
    },
    primary: {
        main: '#00555A',
        dark: '#013A3B',
        light: '#248E9A',
        focus: 'rgb(0, 85, 90,0.12)',
        focusVisible: 'rgb(0, 85, 90,0.3)',
        selected: 'rgb(0, 85, 90,0.08)',
        contrastText: '#FFFFFF',
        ...primitiveColors.primary
    },
    secondary: {
        main: '#00CDBA',
        dark: '#0C7F6A',
        light: '#96FFFB',
        contrastText: '#FFFFFF',
        ...primitiveColors.secondary
    },
    action: {},
    error: {
        main: '#BB3646',
        dark: '#A62A3D',
        light: '#E78589',
        contrastText: '#FFFFFF',
        ...primitiveColors.error
    },
    warning: {
        main: '#EF6C00',
        dark: '#E65100',
        light: '#FF9800',
        contrastText: '#FFFFFF'
    },
    info: {
        main: '#1F6272',
        dark: '#003B48',
        light: '#004FE6',
        contrastText: '#FFFFFF'
    },
    success: {
        main: '#0BAE4B',
        dark: '#0C843C',
        light: '#3EEA82',
        contrastText: '#FFFFFF',
        ...primitiveColors.success
    },
    audit: {
        main: deepPurple['A200'],
        contrastText: '#FFFFFF'
    },
    dryRun: {
        main: pink['600'],
        contrastText: '#FFFFFF'
    },
    subFlow: {
        main: orange['700'],
        contrastText: '#000000'
    },
    common: {}
}

const ThemeInitializer = () => {
    const mode = useAppSelector(s => s.app.mode)

    const theme = createTheme({
        palette: mode === 'dark' ? darkPalette : lightPalette,
        typography: {
            fontFamily: ['"Montserrat"', 'Open Sans'].join(','),
            important: {
                fontSize: '1.1rem',
                fontWeight: 500
            }
        },
        components: {
            MuiButton: {
                styleOverrides: {
                    root: { borderRadius: 24 }
                }
            },
            MuiPaper: {
                styleOverrides: {
                    root: {
                        borderRadius: 12,
                        border: '1px solid #FFFFFF1F'
                    }
                }
            },
            MuiInputBase: {
                styleOverrides: {
                    root: { borderRadius: '6px !important' }
                }
            },
            MuiChip: {
                styleOverrides: {
                    root: {
                        fontFamily: ['"Space Grotesk Variable"', 'Montserrat'].join(',')
                    }
                }
            }
        }
    })

    console.log('rendering ThemeInitializer')
    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <UIInitializer />
        </ThemeProvider>
    )
}

export default ThemeInitializer
